import { useStorageUnits } from 'contexts/storageUnitContext';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import type {
  ROOT,
  StorageUnit,
  StorageUnitIdTypeAndName,
} from 'store/storageunits/types';
import { ExplorerMode, StorageUnitType } from 'store/storageunits/types';
import { useLocalStorage } from 'util/useLocalStorage';
import { canModifyFolder } from 'util/access';
import { isProject } from 'util/isProjectHelper';
import { useSelector } from 'react-redux';
import type { RootState } from 'store';
import { useIsExternal } from 'auth/useIsExternal';

type DragEvent = React.DragEvent<HTMLDivElement>;

export type StorageUnitWithRoot = typeof ROOT | StorageUnit;

type DashboardContextProps = {
  explorerMode: ExplorerMode;
  setExplorerMode: (explorerMode: ExplorerMode) => void;
  dropTarget: string | null;
  dragAndDropProps: {
    onDragStart: (unit: StorageUnitWithRoot) => (e: DragEvent) => void;
    onDragOver: (unit: StorageUnitWithRoot) => (e: DragEvent) => void;
    onDragEnter: (unit: StorageUnitWithRoot) => (e: DragEvent) => void;
    onDragLeave: (unit: StorageUnitWithRoot) => (e: DragEvent) => void;
    onDrop: (unit: StorageUnitWithRoot) => (e: DragEvent) => void;
  };
  selectedIds: string[];
  isSelected: (id: string) => boolean;
  toggleSelected: (id: string) => void;
  selectAll: (ids: string[]) => void;
  clearSelection: () => void;
  recentUnits: StorageUnit[];
  setRecentUnits: (units: StorageUnit[]) => void;
};

const DashboardContext = createContext<DashboardContextProps | null>(null);

export const useDashboard = () => {
  const context = useContext(DashboardContext);

  if (!context) {
    throw new Error('Hook can only be used inside of Dashboard context');
  }

  return context;
};

export const useSelection = () => {
  const context = useContext(DashboardContext);

  if (!context) {
    throw new Error('Hook can only be used inside of Dashboard context');
  }

  return {
    selectedIds: context.selectedIds,
    isSelected: context.isSelected,
    toggleSelected: context.toggleSelected,
    selectAll: context.selectAll,
    clearSelection: context.clearSelection,
    recentUnits: context.recentUnits,
    setRecentUnits: context.setRecentUnits,
  };
};

export const useDragAndDrop = (unit: StorageUnit | typeof ROOT) => {
  const context = useContext(DashboardContext);

  if (!context) {
    throw new Error('Hook can only be used inside of Dashboard context');
  }

  const dragAndDropProps = Object.entries(context.dragAndDropProps).reduce(
    (acc, curr) => {
      return {
        ...acc,
        [curr[0]]: curr[1](unit),
      };
    },
    {},
  );

  return { dropTarget: context.dropTarget, dragAndDropProps };
};

export const DashboardProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { moveUnit } = useStorageUnits();
  const location = useLocation();
  const [explorerMode, setExplorerMode] = useLocalStorage(
    'explorerMode',
    ExplorerMode.Grid,
  );

  const [draggedUnit, setDraggedUnit] =
    useState<StorageUnitIdTypeAndName | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [recentUnits, setRecentUnits] = useState<StorageUnit[]>([]);

  useEffect(() => {
    setSelectedIds([]);
  }, [location.pathname]);

  const isSelected = useCallback(
    (id: string) => selectedIds.includes(id),
    [selectedIds],
  );

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  }, []);

  const selectAll = useCallback((ids: string[]) => {
    setSelectedIds(ids);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const { folderPath } = useSelector(
    (state: RootState) => state.storageUnitsState,
  );
  const isExternal = useIsExternal();

  const onDragStart = (unit: StorageUnitWithRoot) => (e: DragEvent) => {
    e.dataTransfer.setData('id', unit.id);
    e.dataTransfer.setData('type', unit.type);
    e.dataTransfer.setData('name', unit.name);

    const isInProject = folderPath.some((ancestor) => isProject(ancestor));
    e.dataTransfer.setData('isInProject', isInProject.toString());

    setDraggedUnit(unit);
  };

  const canDropOn = (
    unit: StorageUnitWithRoot,
    draggedFileIsInProject?: boolean,
  ): boolean => {
    if (unit.type === StorageUnitType.File) {
      return false;
    }
    if (!canModifyFolder(unit)) {
      return false;
    }

    if (isExternal && draggedUnit?.type === StorageUnitType.File) {
      const targetIsProject = 'project' in unit && isProject(unit);

      if (targetIsProject && !draggedFileIsInProject) {
        return false;
      }
      if (!targetIsProject && draggedFileIsInProject) {
        return false;
      }
    }

    if ('project' in unit && isProject(unit)) {
      return draggedUnit?.type === StorageUnitType.File;
    }
    return true;
  };

  const onDragOver = (unit: StorageUnitWithRoot) => (e: DragEvent) => {
    const draggedFileIsInProject =
      e.dataTransfer.getData('isInProject') === 'true';
    if (
      draggedUnit?.id !== unit.id &&
      canDropOn(unit, draggedFileIsInProject)
    ) {
      setDropTarget(unit.id);
      e.preventDefault();
    }
  };

  const onDragEnter = (unit: StorageUnitWithRoot) => (e: DragEvent) => {
    const draggedFileIsInProject =
      e.dataTransfer.getData('isInProject') === 'true';
    if (unit.id !== dropTarget && canDropOn(unit, draggedFileIsInProject)) {
      setDropTarget(unit.id);
      e.preventDefault();
    }
  };

  const onDragLeave =
    (unit: StorageUnitWithRoot) =>
    (_e: DragEvent): void => {
      if (unit.id === dropTarget) {
        setDropTarget(null);
      }
    };

  const onDrop = (unit: StorageUnitWithRoot) => (e: DragEvent) => {
    const draggedFileIsInProject =
      e.dataTransfer.getData('isInProject') === 'true';
    if (!canDropOn(unit, draggedFileIsInProject)) {
      return;
    }

    const draggedId = e.dataTransfer.getData('id');
    const draggedType = e.dataTransfer.getData('type');
    const draggedName = e.dataTransfer.getData('name');

    if (draggedId !== unit.id) {
      e.preventDefault();
      moveUnit(draggedId, draggedName, unit.id, draggedType as StorageUnitType);
      setDropTarget(null);
    }
  };

  return (
    <DashboardContext.Provider
      value={{
        explorerMode,
        setExplorerMode,
        dropTarget,
        dragAndDropProps: {
          onDragEnter,
          onDragLeave,
          onDragOver,
          onDragStart,
          onDrop,
        },
        selectedIds,
        isSelected,
        toggleSelected,
        selectAll,
        clearSelection,
        recentUnits,
        setRecentUnits,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};
